import type { LoginRequest, TokenResponse } from '@ctcells/contract'
import { API_BASE_URL, clientIdentity, useSessionStore, type SessionEndReason } from '@ctcells/core'

import { postJsonUnauthenticated } from '../client.ts'
import { clearControlLock } from '../controlToken.ts'
import { ApiError } from '../errors.ts'
import {
  clearTokens,
  getRefreshToken,
  getStoredSessionId,
  getStoredUser,
  setAccessToken,
  setRefreshToken,
  getStoredOperations,
  setStoredOperations,
  setStoredSessionId,
  setStoredUser,
} from './tokenStore.ts'
import { loadOperations } from './me.ts'

/**
 * 인증 4연산 — `POST /auth/login` · `/auth/refresh` · `/auth/logout`.
 *
 * 세 경로 모두 계약(정본)과 실물에 **둘 다 있고 실제로 돈다**(2026-09-17 실측).
 * `logout` 만 Bearer 가 필요해서 클라이언트를 통해 나간다.
 */

/** 세션이 끝났을 때 앱이 정리할 것들. `configureSessionTeardown` 으로 주입한다 */
let onSessionEnd: (() => void) | null = null

/**
 * 세션 종료 시 실행할 정리(쿼리 캐시 비우기 · 구독 닫기)를 등록한다.
 * 🔴 `@ctcells/api` 가 QueryClient 인스턴스를 직접 들고 있지 않으려는 것이다 — 주인은 앱 셸이다.
 */
export function configureSessionTeardown(teardown: (() => void) | null): void {
  onSessionEnd = teardown
}

/**
 * 토큰 응답을 반영한다.
 *
 * 🔴 **`operations`(권한 판정 원천)는 세션에 묶인다.**
 *    - 로그인(`reloadOperations`) → `GET /auth/me` 1회
 *    - 같은 세션의 주기 갱신 → 목록 유지 · **다시 묻지 않는다**(폴링 아님 · DP-01)
 *    - 갱신 응답의 세션 id 가 쥐고 있던 것과 다르다 → 목록을 버리고(그동안 역할 표) 1회 묻는다
 *    ⚠ 부팅 복구의 1회는 `restoreSession` 이 따로 부른다 — 그때는 쥐고 있던 세션이 저장값이라 여기서 못 가른다.
 */
function applyTokens(response: TokenResponse, reloadOperations: boolean): void {
  const heldSession = useSessionStore.getState().sessionId
  const sameSession = heldSession === response.session_id
  const rotated = heldSession !== null && !sameSession
  setAccessToken(response.access_token, response.expires_in)
  if (response.refresh_token !== undefined) setRefreshToken(response.refresh_token)
  setStoredSessionId(response.session_id)
  if (!sameSession) setStoredOperations(null)

  // ⚠ refresh 응답에는 user 가 없다. 그때는 저장해 둔 값을 유지한다
  if (response.user !== undefined) setStoredUser(response.user)

  const user = response.user ?? getStoredUser()
  useSessionStore.getState().setAuthenticated(user, response.session_id)
  if (reloadOperations || rotated) void loadOperations()
}

export async function login(credentials: {
  username: string
  password: string
  remember?: boolean
}): Promise<TokenResponse> {
  const body: LoginRequest = {
    username: credentials.username,
    password: credentials.password,
    // 🔴 기기별 표지(`web/<browser>-<os>/<표지>`) — 같은 브라우저의 세션이 「내 계정」에서 한 기기로 묶인다
    client: clientIdentity(),
    ...(credentials.remember === undefined ? {} : { remember_me: credentials.remember }),
  }
  const response = await postJsonUnauthenticated<TokenResponse>(API_BASE_URL, '/auth/login', body)
  applyTokens(response, true)
  return response
}

/**
 * 세션 종료. **호출자를 가리지 않고 같은 정리를 한다.**
 *
 * 🔴 제어락은 우리가 반납하지 않는다 — 세션이 죽으면 서버가 리스를 회수한다(서버 SSOT).
 *    `DELETE /control-token` 을 자동으로 쏘는 것은 상태 추론이다. 보관만 비운다.
 * 🔴 **왜 끝났는지를 같이 남긴다.** 로그인 화면이 그 한 줄을 말한다 — 종전에는 갑자기
 *    로그인 화면으로 튕기기만 하고 **화면이 아무 말도 안 했다**(2026-09-22).
 *    기본값이 `expired` 인 이유는 이 함수를 부르는 자리가 전부 「세션이 죽었다」라서다.
 */
export function endSession(reason: SessionEndReason = 'expired'): void {
  clearTokens()
  clearControlLock()
  useSessionStore.getState().setAnonymous(reason)
  onSessionEnd?.()
}

/**
 * access 토큰 갱신.
 *
 * 🔴 **single-flight 다.** 화면 하나가 쿼리 5개를 동시에 쏘면 401 이 5번 오는데,
 *    refresh 가 **회전형**(새 refresh 토큰이 같이 온다 · 실측)이라 동시에 5번 부르면
 *    서로의 토큰을 무효화한다. 진행 중이면 그 Promise 를 기다린다.
 */
let inFlight: Promise<boolean> | null = null

export function refreshSession(): Promise<boolean> {
  inFlight ??= runRefresh().finally(() => {
    inFlight = null
  })
  return inFlight
}

async function runRefresh(): Promise<boolean> {
  const refresh_token = getRefreshToken()
  if (refresh_token === null) {
    endSession()
    return false
  }

  try {
    const response = await postJsonUnauthenticated<TokenResponse>(API_BASE_URL, '/auth/refresh', {
      refresh_token,
    })
    applyTokens(response, false)
    return true
  } catch (error) {
    // 4xx 는 세션이 죽은 것이다. 전송 오류(서버 다운·네트워크)면 자격을 버리지 않는다 —
    // 잠깐 끊긴 것으로 로그아웃시키면 현장에서 못 쓴다.
    if (error instanceof ApiError && !error.transport) {
      endSession()
      return false
    }
    return false
  }
}

/**
 * 부팅 시 세션 복구. `sessionStorage` 에 refresh 토큰이 있으면 조용히 갱신해 본다.
 *
 * 🔴 그동안 화면은 **로딩**이다(`RequireAuth`). 통과시키면 권한을 모르는 채로 화면이 뜬다.
 */
export async function restoreSession(): Promise<boolean> {
  const store = useSessionStore.getState()
  const refresh_token = getRefreshToken()

  if (refresh_token === null) {
    store.setAnonymous()
    return false
  }

  // 저장해 둔 user 를 먼저 얹어 둔다 — 갱신이 성공해도 응답에 user 가 없기 때문이다
  // 🔴 저장해 둔 `operations` 도 같이 얹는다 — 새로고침 직후 `/auth/me` 가 오기 전에도 판정이 선다
  const stored = getStoredUser()
  if (stored !== null) store.setAuthenticated(stored, getStoredSessionId(), getStoredOperations())

  const ok = await refreshSession()
  // 부팅 뒤 1회는 늘 다시 묻는다 — 떠나 있던 사이 서버에서 역할이 바뀌었을 수 있다(진행 중이면 합류)
  if (ok) void loadOperations()
  /*
   * 🔴 **이미 적힌 사유를 지우지 않는다.** 갱신이 4xx 로 죽으면 `refreshSession` 안에서
   *    `endSession('expired')` 가 먼저 돌아 상태가 이미 `anonymous` 다.
   * ⚠ 여기 남는 갈래는 **전송 오류**(서버 다운 · 회선 끊김)뿐이라 사유를 안 붙인다 —
   *   만료가 아닌데 「만료되었습니다」로 적으면 «못 봤다»를 «확정된 사실»로 접는 것이다.
   */
  if (!ok && useSessionStore.getState().status !== 'anonymous') store.setAnonymous()
  return ok
}
